import { Box, Loader, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys } from '../../../../../types';
import { selectAttaches } from '../../../../common/selectors/attaches';
import { attachmentsStatusDataHooks } from './AttachmentsStatus.dataHooks';

type AttachmentsStatusProps = {
  onFilesUploadCancel: () => void;
};

// After 3 seconds(after starting the upload) a text button will be displayed that allows the upload to be canceled
const CANCEL_BUTTON_APPEAR_TIMEOUT = 3000; // ms

export const AttachmentsStatus: FC<AttachmentsStatusProps> = ({ onFilesUploadCancel }) => {
  const { t } = useTranslation();
  const { isLoading, loadingFilesCount = 0 } = useSelector(selectAttaches);
  const [showCancelButton, setShowCancelButton] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => setShowCancelButton(true), CANCEL_BUTTON_APPEAR_TIMEOUT);

      return () => {
        clearTimeout(timeoutId);
        setShowCancelButton(false);
      };
    }
  }, [isLoading]);

  if (!isLoading) {
    return null;
  }

  return (
    <Box gap={1} verticalAlign="middle">
      <Loader size="tiny" dataHook={attachmentsStatusDataHooks.loader()} />
      {showCancelButton ? (
        <TextButton
          size="small"
          onClick={onFilesUploadCancel}
          dataHook={attachmentsStatusDataHooks.cancelButton()}
        >
          {t<TLocalesKeys>('attach_status_cancel_upload')}
        </TextButton>
      ) : (
        <Text size="small" secondary dataHook={attachmentsStatusDataHooks.statusText()}>
          {t<TLocalesKeys>(
            loadingFilesCount > 1 ? 'attach_status_uploading_files' : 'attach_status_uploading_file',
          )}
        </Text>
      )}
    </Box>
  );
};
